import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { TrendingUp, TrendingDown, Calendar, DollarSign, X, Filter } from 'lucide-react';
import { cashflowApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const CashflowDashboard = ({ onClose }) => {
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [filterType, setFilterType] = useState('all'); // all, revenue, expense
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, [selectedPeriod]);

  const loadData = async () => {
    try {
      setLoading(true);
      const days = parseInt(selectedPeriod);
      
      const [timelineRes, summaryRes] = await Promise.all([
        cashflowApi.getTimeline('default_company', days),
        cashflowApi.getSummary('default_company', days)
      ]);
      
      setTimeline(timelineRes.data || []);
      setSummary(summaryRes.data || {});
    } catch (error) {
      console.error('Error loading cashflow data:', error);
      toast({
        title: "Fehler",
        description: "Cashflow-Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredTimeline = timeline.filter(item => {
    if (filterType === 'all') return true;
    return item.type === filterType;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Heute';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Morgen';
    }
    
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: 'short',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const getSummaryForPeriod = () => {
    if (selectedPeriod === '30') return summary.next30Days || {};
    if (selectedPeriod === '60') return summary.next60Days || {};
    return summary.next90Days || {};
  };

  const periodSummary = getSummaryForPeriod();
  const isPositive = (periodSummary.net || 0) >= 0;

  return (
    <div className="space-y-6 p-6">
      {/* Period Selector */}
      <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="30">30 Tage</TabsTrigger>
          <TabsTrigger value="60">60 Tage</TabsTrigger>
          <TabsTrigger value="90">90 Tage</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Erwartete Einnahmen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{(periodSummary.revenue || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {summary.upcomingCount?.revenue || 0} Zahlungen
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              Erwartete Ausgaben
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              -{(periodSummary.expenses || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {summary.upcomingCount?.expenses || 0} Zahlungen
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">
              Erwarteter Netto-Cashflow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? '+' : ''}{(periodSummary.net || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Nächste {selectedPeriod} Tage
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={filterType === 'all' ? 'default' : 'outline'}
          onClick={() => setFilterType('all')}
        >
          Alle
        </Button>
        <Button
          size="sm"
          variant={filterType === 'revenue' ? 'default' : 'outline'}
          onClick={() => setFilterType('revenue')}
          className={filterType === 'revenue' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          Einnahmen
        </Button>
        <Button
          size="sm"
          variant={filterType === 'expense' ? 'default' : 'outline'}
          onClick={() => setFilterType('expense')}
          className={filterType === 'expense' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          Ausgaben
        </Button>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Zahlungs-Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Lade Daten...</div>
          ) : filteredTimeline.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Keine Zahlungen für diesen Zeitraum</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredTimeline.map((item, index) => {
                const isRevenue = item.type === 'revenue';
                const showDateHeader = index === 0 || item.date !== filteredTimeline[index - 1].date;
                
                return (
                  <React.Fragment key={`${item.id}-${item.date}`}>
                    {showDateHeader && (
                      <div className="flex items-center gap-2 pt-4 pb-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-semibold text-slate-700">
                          {formatDate(item.date)}
                        </span>
                        <span className="text-xs text-slate-500">
                          ({new Date(item.date).toLocaleDateString('de-DE', { weekday: 'long' })})
                        </span>
                      </div>
                    )}
                    
                    <div className={`flex items-center justify-between p-3 rounded-lg border ${
                      isRevenue 
                        ? 'border-green-200 bg-green-50 hover:bg-green-100' 
                        : 'border-red-200 bg-red-50 hover:bg-red-100'
                    }`}>
                      <div className="flex items-center gap-3 flex-1">
                        {isRevenue ? (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{item.title}</span>
                            {item.isRecurring && (
                              <Badge variant="outline" className="text-xs">
                                Wiederkehrend
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {isRevenue ? 'Einnahme' : 'Ausgabe'} · {item.category}
                          </div>
                        </div>
                      </div>
                      
                      <div className={`text-lg font-bold ${
                        isRevenue ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {isRevenue ? '+' : '-'}{item.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Running Balance Preview (optional) */}
      {filteredTimeline.length > 0 && filterType === 'all' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Kumulierter Saldo-Verlauf</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(() => {
                let runningBalance = 0;
                return filteredTimeline.reduce((acc, item, index) => {
                  runningBalance += item.type === 'revenue' ? item.amount : -item.amount;
                  
                  // Only show every 5th item or last item to avoid clutter
                  if (index % 5 === 0 || index === filteredTimeline.length - 1) {
                    acc.push(
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span className="text-slate-600">
                          Nach {formatDate(item.date)}:
                        </span>
                        <span className={`font-semibold ${
                          runningBalance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {runningBalance >= 0 ? '+' : ''}{runningBalance.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                        </span>
                      </div>
                    );
                  }
                  return acc;
                }, []);
              })()}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CashflowDashboard;
